// src/components/puzzleConfig.ts — PUZZLE CONFIGURATION
// =====================================================================
//  ① TRANSFORMS  – positions, rotations, scales, animation timing
//  ② LIGHTING    – intensities, colours, glow, fade, shader params
// =====================================================================

import * as THREE from "three";

// =====================================================================
//  ① TRANSFORMS
// =====================================================================

// ─────────────────────────────────────────────────────────────
// Camera
// Implicit tilt = atan2(lookAt.y − pos.y, pos.z − lookAt.z)
// To tilt more upward: raise lookAt.y or lower pos.y.
// ─────────────────────────────────────────────────────────────

export const CAMERA_POS:    [number, number, number] = [0, 0, 10]; // world position [x, y, z]
export const CAMERA_LOOKAT: [number, number, number] = [0, 0, 0];   // look-at target [x, y, z]
export const CAMERA_ZOOM_START      = 280; // orthographic zoom before solve (higher = larger)
export const CAMERA_ZOOM_SOLVED     = 200; // orthographic zoom after solve
export const CAMERA_ZOOM_LERP_SPEED = 5.0; // lerp speed between the two zoom levels
export const SCENE_Y_OFFSET = 0; // shifts the whole puzzle group up / down (stacks with lookAt framing)

// ─────────────────────────────────────────────────────────────
// Piece — initial placement and drag control
// ─────────────────────────────────────────────────────────────

export const PIECE_START_Z  =  1.5;  // hover depth (+ = toward viewer)
export const PIECE_SOLVE_Z  = -0.5;  // depth when embedded into socket

export const PIECE_INITIAL_ROT_X_DEG =  135;   // starting pitch (degrees)
export const PIECE_INITIAL_ROT_Y_DEG = 150;   // starting yaw (degrees)
export const ROT_SPEED = 0.014;                // drag sensitivity (rad / px)

// Target orientation for solve — piece must reach this rotation to trigger lock
export const SOLVE_ROT_DEG = { x: 0, y: 0, z: 0 }; // degrees, relative to modelled identity

// ─────────────────────────────────────────────────────────────
// Socket / Background — pre-solve looming effect and settle
// ─────────────────────────────────────────────────────────────

export const BG_Z = 0; // socket wall depth

// ─────────────────────────────────────────────────────────────
// Solve & animation timing
// ─────────────────────────────────────────────────────────────

export const MAX_GLOW_ANGLE   = THREE.MathUtils.degToRad(60); // angular radius of proximity glow cone — piece glows as it enters this cone
export const SOLVE_ANGLE_EPS  = THREE.MathUtils.degToRad(15); // angular radius of solve-lock cone — piece must be inside this to start dwell timer
export const SOLVE_DWELL_TIME = 0.35; // seconds piece must hold inside solve cone before auto-align triggers

export const ALIGN_DURATION = 0.4; // seconds for auto-align animation (rotation + slide to center)
export const EMBED_DURATION = 0.4; // seconds for embed animation (slide into socket at PIECE_SOLVE_Z)

// ─────────────────────────────────────────────────────────────
// Derived transforms  (computed from above — do not edit directly)
// ─────────────────────────────────────────────────────────────

export const TARGET_SOLVE_ROT = new THREE.Euler(
  THREE.MathUtils.degToRad(SOLVE_ROT_DEG.x),
  THREE.MathUtils.degToRad(SOLVE_ROT_DEG.y),
  THREE.MathUtils.degToRad(SOLVE_ROT_DEG.z)
);

export const PIECE_START_POS  = new THREE.Vector3(0, 0, PIECE_START_Z);
export const TARGET_ALIGN_POS = new THREE.Vector3(0, 0, PIECE_START_Z); // center XY, same Z as start (mid-air aligned)
export const FINAL_EMBED_POS  = new THREE.Vector3(0, 0, PIECE_SOLVE_Z); // center XY, pushed into socket

// =====================================================================
//  ② LIGHTING
//
//  5 lights total:
//    1. Ambient          — fills entire scene; dims during solve to make glow pop     [PuzzleScene]
//    2. Directional key  — casts shadows; moves forward + brightens during solve      [PuzzleScene]
//    3. Directional fill — static low fill from lower-left; constant intensity        [PuzzleScene]
//    4. Front SpotLight  — viewer-facing key on piece; fades OFF once solved          [PuzzlePiece]
//    5. Point light      — co-located with piece, green; driven by emissive intensity [PuzzlePiece]
//
//  State transitions:
//    idle      → ambient BASE,      dir BASE,      fill on, front spot ON,  point = proximity
//    solving   → ambient HIGHLIGHT, dir HIGHLIGHT, fill on, front spot ON→fading, point = full
//    done      → ambient → 0,       dir → 0,       fill → 0, front spot OFF, point = flicker loop
// =====================================================================

// ─────────────────────────────────────────────────────────────
// Scene — canvas background and global fade-in
// ─────────────────────────────────────────────────────────────

export const SCENE_BG_COLOR         = "#000000"; // canvas clear colour
export const SCENE_FADE_IN_DURATION = 1.0;       // seconds to fade all lights from 0 on load (prevents flash)

// ─────────────────────────────────────────────────────────────
// Light 1 — Ambient
// Fills the whole scene uniformly; dims during solve to make piece glow stand out;
// fades fully to 0 post-solve (controlled by LIGHT_SPEED_FADE).
// ─────────────────────────────────────────────────────────────

export const AMBIENT_BASE      = 0.25; // intensity at rest (idle)
export const AMBIENT_HIGHLIGHT = 0.55; // intensity during solve approach (lower = more dramatic)

// ─────────────────────────────────────────────────────────────
// Light 2 — Directional key  (casts shadows, moves on solve)
// Starts at rest, then moves toward viewer and brightens as piece enters solve state;
// fades fully to 0 post-solve.
// ─────────────────────────────────────────────────────────────

export const DIR_INTENSITY_BASE      = 0.1; // intensity at rest
export const DIR_INTENSITY_HIGHLIGHT = 0.3; // intensity at peak solve

export const DIR_POS_INITIAL: [number, number, number] = [3, 5, 5]; // JSX initial position (sets shadow angle)
export const DIR_POS_BASE      = new THREE.Vector3(0, 0, 1);        // animated rest position
export const DIR_POS_HIGHLIGHT = new THREE.Vector3(0, 0, 7);        // animated solve position (straight-on)

// ─────────────────────────────────────────────────────────────
// Light 3 — Directional fill  (static, lower-left, constant)
// Always-on low-intensity fill from the lower-left; does not respond to solve state.
// Fades in with the scene on load, then stays constant.
// ─────────────────────────────────────────────────────────────

export const FILL_POS: [number, number, number] = [-3, -2, 1]; // world position of fill light
export const FILL_INTENSITY_BASE = 0.5;                        // constant intensity (no highlight/fade states)

// Shared lerp speeds for lights 1 + 2
export const LIGHT_SPEED_HIGHLIGHT = 1;   // lerp speed into solve highlight (higher = snappier)
export const LIGHT_SPEED_FADE      = 8; // lerp speed for post-solve fade to 0

// ─────────────────────────────────────────────────────────────
// Light 4 — Front SpotLight  (viewer-facing key on piece)
// Positioned directly in front of the scene at z = FRONT_SPOT_DISTANCE, aimed at origin.
// Active during idle / aligning / embedding, then fades OFF on solve.
// Dominates pre-solve appearance at intensity 100.
// ─────────────────────────────────────────────────────────────

export const FRONT_SPOT_DISTANCE   = 10.0;        // world-unit reach of the spot cone
export const FRONT_SPOT_INTENSITY  = 5;         // peak intensity (pre-solve)
export const FRONT_SPOT_ANGLE      = Math.PI / 3; // half-angle of cone (radians)
export const FRONT_SPOT_PENUMBRA   = 3;           // softness of cone edge (0 = hard, higher = softer)
export const FRONT_SPOT_DECAY      = 0.5;         // physical falloff exponent
export const FRONT_SPOT_FADE_SPEED = 8.0;         // lerp speed fading to 0 after solve

// ─────────────────────────────────────────────────────────────
// Light 5 — Point light  (co-located with piece, green)
// Driven by the same intensity curve as the piece emissive:
//   pointIntensity = emissiveIntensity × POINT_LIGHT_INTENSITY_MULT
// Flickers in sync with post-solve emissive flicker.
// ─────────────────────────────────────────────────────────────

export const POINT_LIGHT_DISTANCE       = 5;                        // world-unit reach
export const POINT_LIGHT_DECAY          = 0.7;                       // falloff exponent
export const POINT_LIGHT_INTENSITY_MULT = 6;                        // multiplier: intensity = emissive × this
export const POINT_LIGHT_OFFSET        = new THREE.Vector3(0, 0, 0); // offset pre-solve
export const POINT_LIGHT_OFFSET_SOLVED = new THREE.Vector3(0, 0, 4.7);   // offset post-solve (lerps on solve)
export const POINT_LIGHT_OFFSET_LERP_SPEED = 15.0;                      // lerp speed between the two offsets

// ─────────────────────────────────────────────────────────────
// Piece emissive glow  (material property, not a Three.js light)
// Applied to all MeshStandardMaterial meshes on the piece.
// Proximity (0–1) lerps intensity from BASE to MAX during idle;
// locked at MAX during solve phases.
// ─────────────────────────────────────────────────────────────

export const GLOW_COLOR          = new THREE.Color("#00ff00"); // emissive colour
export const GLOW_BASE_INTENSITY = 0.25; // always-on minimum (piece never fully dark)
export const GLOW_MAX_INTENSITY  = 0.5;  // full glow when proximity = 1 (perfectly aligned)
export const GLOW_LERP_SPEED     = 6;    // lerp speed for glow transitions (higher = snappier)

// ─────────────────────────────────────────────────────────────
// Post-solve flicker  (applied to emissive + point light in sync)
// Alternates between quiet idle periods and short high-frequency shimmer bursts.
// Burst oscillates emissive between [intensityMin, intensityMax] × baseIntensity.
// ─────────────────────────────────────────────────────────────

export const POST_SOLVE_FLICKER_CONFIG = {
  idleDurationMin: 3.0,   // seconds between bursts (min)
  idleDurationMax: 8.0,   // seconds between bursts (max)
  burstDurationMin: 0.25, // burst length (min)
  burstDurationMax: 0.5,  // burst length (max)
  freqMin: 50,            // shimmer frequency Hz (min)
  freqMax: 70,            // shimmer frequency Hz (max)
  intensityMinRange: [0.1, 0.3]  as const, // emissive low  factor range during burst
  intensityMaxRange: [0.8, 0.95] as const, // emissive high factor range during burst
};

// ─────────────────────────────────────────────────────────────
// Edge darkening shader  (custom onBeforeCompile on piece materials)
// Darkens fragments near the silhouette based on view-space dot product.
// Runs entirely on the GPU — no runtime JS cost.
// ─────────────────────────────────────────────────────────────

export const EDGE_STRENGTH = 0.5;   // 0–1, darkness at silhouette edges (1 = fully black)
export const EDGE_BIAS     = 0.1; // prevents the very centre from being affected

// ─────────────────────────────────────────────────────────────
// Socket highlight shader  (custom shader on socket/BG mesh)
// Simulates a specular highlight from a fixed light direction.
// ─────────────────────────────────────────────────────────────

export const HIGHLIGHT_STRENGTH  = 0.5; // highlight brightness (0 = off, 1 = full)
export const HIGHLIGHT_EXPONENT  = 1.0; // falloff sharpness (higher = tighter spot)
export const HIGHLIGHT_LIGHT_DIR = new THREE.Vector3(-3, -3, -3).normalize(); // virtual light direction

// ─────────────────────────────────────────────────────────────
// Socket glow  (emissive on socket/BG mesh, pulsing)
// Set BASE and PULSE both to 0 to disable entirely.
// To re-enable: BASE ~0.15, PULSE ~0.3.
// ─────────────────────────────────────────────────────────────

export const SOCKET_GLOW_COLOR     = new THREE.Color("#00ff00");
export const SOCKET_EMISSIVE_BASE  = 0;   // constant base emissive intensity (0 = off)
export const SOCKET_EMISSIVE_PULSE = 0;   // sinusoidal pulse amplitude added on top of base (0 = off)
export const SOCKET_PULSE_SPEED    = 1.5; // pulse frequency (Hz)

// =====================================================================
//  Helpers
// =====================================================================

export function shortestAngleDiff(a: number, b: number) {
  let diff = (a - b + Math.PI) % (Math.PI * 2);
  if (diff < 0) diff += Math.PI * 2;
  return diff - Math.PI;
}

export function smoothstep01(t: number) {
  const c = THREE.MathUtils.clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}
